import React, { useState } from 'react';
import { X, Image as ImageIcon, Film, Radio, Send, AtSign, Check, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { currentUser, taggableUsers, places } from '../mock';
import { Avatar, RichText } from './shared';
import MediaStudio from './MediaStudio';
import LiveScreen from './LiveScreen';

export default function CreateModal({ open, onClose }) {
  const [caption, setCaption] = useState('');
  const [showTag, setShowTag] = useState(false);
  const [showLoc, setShowLoc] = useState(false);
  const [tagged, setTagged] = useState([]);
  const [location, setLocation] = useState(null);
  const [studio, setStudio] = useState(null); // 'photo' | 'video'
  const [live, setLive] = useState(false);

  if (studio) return <MediaStudio mode={studio} onClose={() => { setStudio(null); onClose(); }} />;
  if (live) return <LiveScreen onClose={() => { setLive(false); onClose(); }} />;
  if (!open) return null;

  const close = () => { onClose(); setCaption(''); setTagged([]); setLocation(null); setShowTag(false); setShowLoc(false); };
  const mention = (u) => setCaption((c) => `${c}${c && !c.endsWith(' ') ? ' ' : ''}@${u.handle} `);
  const toggleTag = (u) => setTagged((t) => (t.some((x) => x.handle === u.handle) ? t.filter((x) => x.handle !== u.handle) : [...t, u]));
  const post = () => { close(); toast.success('Moment shared', { style: { background: '#0f1622', color: '#fff', border: '1px solid #1c2634' } }); };

  const tiles = [
    { l: 'Photo', Icon: ImageIcon, onClick: () => setStudio('photo'), cls: 'bg-[#122a3a] text-[#2f8bff]' },
    { l: 'Video', Icon: Film, onClick: () => setStudio('video'), cls: 'bg-[#2a2440] text-[#a78bfa]' },
    { l: 'Go Live', Icon: Radio, onClick: () => setLive(true), cls: 'bg-[#3a1220] text-[#f43f5e]' },
  ];

  return (
    <div className="absolute inset-0 z-40 flex items-end" onClick={close}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full bg-[#0c1420] rounded-t-3xl p-5 animate-pop-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-white">Create</h3>
          <button onClick={close}><X size={22} className="text-[#8b95a5]" /></button>
        </div>

        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {tiles.map((t) => (
            <button key={t.l} onClick={t.onClick} className="flex flex-col items-center gap-2 py-4 rounded-2xl bg-[#0a0f18] ring-1 ring-[#1c2634] active:scale-[0.97] transition-transform">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${t.cls}`}><t.Icon size={20} /></div>
              <span className="text-[12px] font-semibold text-white">{t.l}</span>
            </button>
          ))}
        </div>

        <div className="flex items-start gap-3">
          <Avatar src={currentUser.avatar} size={40} />
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2} placeholder="Share a thought... use @ to mention" className="flex-1 bg-transparent text-[15px] text-white placeholder:text-[#5c6675] outline-none resize-none pt-1.5" />
        </div>

        {caption.trim() && <div className="ml-[52px] -mt-1 mb-1"><RichText text={caption} className="text-[13px] text-[#8b95a5]" /></div>}

        <div className="flex flex-wrap gap-1.5 ml-[52px]">
          {tagged.map((t) => <span key={t.handle} className="text-[11px] text-[#2f8bff] bg-[#122a3a] px-2 py-0.5 rounded-full">@{t.handle}</span>)}
          {location && <span className="flex items-center gap-1 text-[11px] text-[#22c55e] bg-[#12322a] px-2 py-0.5 rounded-full"><MapPin size={10} /> {location.name}</span>}
        </div>

        {showTag && (
          <div className="mt-2 max-h-40 overflow-y-auto app-scroll rounded-xl ring-1 ring-[#1c2634] bg-[#0a0f18]">
            {taggableUsers.map((u) => {
              const on = tagged.some((x) => x.handle === u.handle);
              return (
                <button key={u.handle} onClick={() => { toggleTag(u); mention(u); }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#0e1522]">
                  <Avatar src={u.avatar} size={34} />
                  <div className="flex-1 text-left"><p className="text-[13px] font-semibold text-white">{u.name}</p><p className="text-[11px] text-[#8b95a5]">@{u.handle}</p></div>
                  {on && <Check size={16} className="text-[#22c55e]" />}
                </button>
              );
            })}
          </div>
        )}

        {showLoc && (
          <div className="mt-2 max-h-40 overflow-y-auto app-scroll rounded-xl ring-1 ring-[#1c2634] bg-[#0a0f18]">
            {places.map((p) => (
              <button key={p.id} onClick={() => { setLocation(p); setShowLoc(false); }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#0e1522]">
                <img src={p.image} alt="" className="w-9 h-9 rounded-lg object-cover" />
                <div className="flex-1 text-left"><p className="text-[13px] font-semibold text-white">{p.name}</p><p className="text-[11px] text-[#8b95a5]">{p.area} · {p.distance}</p></div>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => { setShowTag((v) => !v); setShowLoc(false); }} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] active:scale-95 transition-transform ${showTag ? 'bg-[#122a3a] text-[#2f8bff]' : 'bg-[#141c28] text-[#c4ccd8]'}`}>
            <AtSign size={16} className="text-[#2f8bff]" /> Tag
          </button>
          <button onClick={() => { setShowLoc((v) => !v); setShowTag(false); }} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] active:scale-95 transition-transform ${showLoc ? 'bg-[#12322a] text-[#22c55e]' : 'bg-[#141c28] text-[#c4ccd8]'}`}>
            <MapPin size={16} className="text-[#22c55e]" /> Location
          </button>
          <button onClick={post} disabled={!caption.trim()} className={`ml-auto flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-semibold transition-colors ${caption.trim() ? 'bg-[#2f8bff] text-white active:scale-95' : 'bg-[#141c28] text-[#5c6675]'}`}>
            <Send size={16} /> Share
          </button>
        </div>
      </div>
    </div>
  );
}
